import { ShieldCheck } from "lucide-react";

import { PrincipalAvatar } from "@/modules/dashboard/settings/components/access/PrincipalAvatar";
import type { ApiKeyEntity } from "@/modules/dashboard/settings/services/apiKeysService";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { useTranslations } from "@/shared/lang/useTranslations";

export interface ApiKeyIdentityCellProps {
  apiKey: ApiKeyEntity;
  lastUsedLabel: string | null;
}

/**
 * Who the key is: name, privilege badge, masked prefix, IP restriction and last use.
 *
 * The prefix is the only part of the secret that survives creation (`toMasked` keeps six leading and four
 * trailing characters), and it is what an operator matches against a log line — so it sits under the name
 * rather than in a column of its own.
 *
 * "Cualquier IP" is stated rather than left blank: an empty allow-list means the key works from anywhere,
 * which is a security posture and not missing data.
 */
export function ApiKeyIdentityCell({ apiKey, lastUsedLabel }: ApiKeyIdentityCellProps) {
  const { t } = useTranslations("settings");
  const ipCount = apiKey.allowed_ips?.length ?? 0;

  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <PrincipalAvatar name={apiKey.name} kind="key" />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-[13px] font-medium text-foreground">{apiKey.name}</span>
          {apiKey.super_admin ? (
            <ColocaBadge variant="destructive" icon={ShieldCheck}>
              {t("access_apikeys_super_badge")}
            </ColocaBadge>
          ) : (
            apiKey.is_admin && (
              <ColocaBadge variant="warning" icon={ShieldCheck}>
                {t("access_apikeys_admin_badge")}
              </ColocaBadge>
            )
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 truncate font-mono text-[11px] text-muted-foreground">
          <span className="truncate">{apiKey.key}</span>
          <span>·</span>
          <span className="whitespace-nowrap">
            {ipCount === 0
              ? t("access_apikeys_any_ip")
              : ipCount === 1
                ? t("access_apikeys_ip_count_singular")
                : t("access_apikeys_ip_count_plural", { count: ipCount })}
          </span>
          {lastUsedLabel && (
            <>
              <span>·</span>
              <span className="whitespace-nowrap">{lastUsedLabel}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
